import { ReactNode } from 'react';

export type AccountStatusVariant = 'active' | 'inactive' | 'paper' | 'live';
export type AccountType = 'paper' | 'live';

export interface AccountStatus {
  label: string;
  /** @deprecated Prefer `variant` */
  active?: boolean;
  variant?: AccountStatusVariant;
  title?: string;
  /** When set, renders a status dot before the label (true = pulsing on) */
  activeDot?: boolean;
}

export interface AccountCardProps {
  title: string;
  accountType?: AccountType;
  statuses: AccountStatus[];
  actions?: ReactNode;
  children: ReactNode;
}

// Card frame recipe (translated from `.portfolio-item` / `--live` in accounts.css).
const CARD_FRAME =
  'rounded-[var(--radius-xl,12px)] border bg-[color:var(--nq-surface-card)] p-5 backdrop-blur-[10px] transition-[border-color,box-shadow] duration-200';
const CARD_FRAME_PAPER =
  'border-[color:var(--border-slate)] hover:border-primary hover:shadow-[var(--shadow-card-soft)]';
const CARD_FRAME_LIVE =
  'border-secondary/55 shadow-[0_0_0_1px_rgba(247,148,29,0.08)] hover:border-secondary/75 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_0_0_1px_rgba(247,148,29,0.12)]';

const resolveStatusVariant = (status: AccountStatus): AccountStatusVariant => {
  if (status.variant) return status.variant;
  return status.active ? 'active' : 'inactive';
};

const AccountCard = ({
  title,
  accountType,
  statuses,
  actions,
  children,
}: AccountCardProps) => {
  const hasStatuses = statuses.length > 0;
  const hasActions = Boolean(actions);

  return (
    // `portfolio-item` is retained as the integration hook the (intentionally
    // kept) PortfolioCompareUx.css selection ring targets (`> .portfolio-item`).
    // The frame itself is token classes (was `.portfolio-item` in accounts.css,
    // which the ledger no longer imports) so both frontends render the same box.
    <div
      className={`portfolio-item${
        accountType ? ` portfolio-item--${accountType}` : ''
      } ${CARD_FRAME} ${accountType === 'live' ? CARD_FRAME_LIVE : CARD_FRAME_PAPER}`}
    >
      <div className="mb-[15px] flex flex-wrap items-start justify-between gap-4">
        <h3
          className={`m-0 min-w-0 flex-[1_1_180px] font-display text-[22px] font-bold leading-[1.3] ${
            accountType === 'paper' ? 'text-primary' : 'text-secondary'
          }`}
        >
          {title}
        </h3>
        {(hasStatuses || hasActions) && (
          <div className="flex flex-[1_1_auto] flex-wrap items-center justify-end gap-2.5">
            {hasStatuses && (
              <div
                className="inline-flex items-center gap-2"
                role="status"
                aria-label="Account status"
              >
                {statuses.map((status) => {
                  const variant = resolveStatusVariant(status);
                  const tone = {
                    active:
                      'bg-positive/12 text-positive border-positive/35',
                    inactive:
                      'bg-muted text-[rgba(255,255,255,0.65)] border-[rgba(255,255,255,0.18)]',
                    paper:
                      'bg-primary/12 text-[#7dd3fc] border-primary/35',
                    live: 'bg-secondary/12 text-secondary border-secondary/35',
                  }[variant];
                  const dotOn =
                    variant === 'paper'
                      ? 'bg-primary'
                      : variant === 'live'
                        ? 'bg-secondary'
                        : 'bg-positive';
                  return (
                    <span
                      key={status.label}
                      className={`pointer-events-none inline-flex min-h-[28px] select-none items-center justify-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase leading-none tracking-[0.04em] ${tone}`}
                      title={status.title}
                    >
                      {status.activeDot !== undefined && (
                        <span
                          className={`inline-block h-[8px] w-[8px] shrink-0 rounded-full ${
                            status.activeDot
                              ? `${dotOn} motion-safe:animate-[nq-pulse_2s_ease-in-out_infinite]`
                              : 'bg-[rgba(255,255,255,0.35)]'
                          }`}
                          aria-hidden
                        />
                      )}
                      {status.label}
                    </span>
                  );
                })}
              </div>
            )}
            {hasStatuses && hasActions && (
              <span
                className="select-none px-0.5 text-[18px] font-light leading-none text-[rgba(255,255,255,0.28)]"
                aria-hidden="true"
              >
                |
              </span>
            )}
            {hasActions && (
              <div className="inline-flex flex-wrap items-center gap-2">{actions}</div>
            )}
          </div>
        )}
      </div>
      <div className="text-sm text-muted-foreground">{children}</div>
    </div>
  );
};

export default AccountCard;
